import { api } from "../api";
import { offerAfterSignIn } from "../push";
import { S, on, render } from "../state";
import { $, esc, toast } from "../ui";
import type { MeResponse } from "../../shared/types";

const ARROW = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>`;

/**
 * Sign-in is just the name badge: you type your email where the name would be, then the 6-digit code in the
 * same spot. The note about the emailed code sits under the badge.
 */
export function signinView() {
  const st = S.signin;
  const field =
    st.step === "email"
      ? `<form data-submit="send-code" class="sform" novalidate>
    <label class="sr" for="em">Email Address</label>
    <input class="bfield" type="email" id="em" value="${esc(st.email)}" placeholder="Enter Email Address" autocomplete="email" inputmode="email" autocapitalize="off" spellcheck="false" data-input="em-fit" required>
    <button class="go" type="submit" aria-label="Email Me a Code">${ARROW}</button></form>`
      : `<form data-submit="verify" class="sform" novalidate><div id="code">
    <label class="sr" for="cd">6-Digit Code</label>
    <input class="bfield bcode" type="text" id="cd" inputmode="numeric" pattern="[0-9]*" maxlength="6" placeholder="Enter 6-Digit Code" autocomplete="one-time-code" data-input="code-digit"></div>
    <button class="go" type="submit" aria-label="Sign In">${ARROW}</button></form>`;
  const note =
    st.step === "code"
      ? `<div class="snote"><p>If <b>${esc(st.email)}</b> is on file, we just emailed it a 6-digit code. It expires in 15 minutes.</p>
    <div class="row" style="justify-content:center;gap:4px;flex-wrap:wrap"><button class="slink" type="button" data-act="signin-back">Use a Different Email</button><span aria-hidden="true">·</span><button class="slink" type="button" data-act="resend-code">Resend Code</button></div>
    <p class="small">No email after a few minutes? Check your spam folder, or contact the Admissions Team.</p></div>`
      : "";
  return `<div class="signin"><div class="sbadge">
    <img class="blogo" src="/brand/mcds-logo.png" alt="Miami Country Day School" width="97" height="82">
    ${field}
    <div class="err" id="err" role="alert"></div></div>
    ${note}
    <p class="sfoot"><span>MCDS Ambassador Scheduling ·</span> <span>Built with Spartan Pride 💙</span></p></div>`;
}

const setErr = (m: string) => {
  const e = $("#err");
  if (e) e.textContent = m;
};
const busy = (f: HTMLElement, on: boolean) => f.querySelectorAll("button").forEach((b) => ((b as HTMLButtonElement).disabled = on));

function fitField(inp: HTMLInputElement) {
  inp.style.fontSize = "";
  let size = parseFloat(getComputedStyle(inp).fontSize);
  while (inp.scrollWidth > inp.clientWidth && size > 13) {
    size -= 1;
    inp.style.fontSize = size + "px";
  }
}

function digits() {
  return ($<HTMLInputElement>("#cd")?.value || "").replace(/\D/g, "");
}

on({
  "send-code": async (f) => {
    const email = ($("#em") as HTMLInputElement).value.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return setErr("Enter a valid email address.");
    busy(f, true);
    try {
      await api("/api/auth/code", { body: { email } });
      S.signin = { step: "code", email };
      render();
    } catch (e) {
      setErr((e as Error).message);
      busy(f, false);
    }
  },
  "resend-code": async () => {
    try {
      await api("/api/auth/code", { body: { email: S.signin.email } });
      toast("New code sent. Use the newest email.");
    } catch (e) {
      setErr((e as Error).message);
    }
  },
  "signin-back": () => {
    S.signin.step = "email";
    render();
  },
  // Long addresses shrink to fit on the badge instead of scrolling out of view.
  "em-fit": (el) => fitField(el as HTMLInputElement),
  "code-digit": (el) => {
    const inp = el as HTMLInputElement;
    inp.value = inp.value.replace(/\D/g, "").slice(0, 6);
    if (inp.value.length === 6) (inp.closest("form") as HTMLFormElement).requestSubmit();
  },
  verify: async (f) => {
    const code = digits();
    if (code.length !== 6) return setErr("Enter all 6 digits.");
    busy(f, true);
    try {
      S.me = await api<MeResponse>("/api/auth/verify", { body: { email: S.signin.email, code } });
      // Re-read to pick up anything that only comes with a full load, like a season card.
      S.me = await api<MeResponse>("/api/me").catch(() => S.me);
      S.signin = { step: "email", email: "" };
      if (!location.hash || location.hash === "#signin") location.hash = "#home";
      render();
      toast("Signed in. This device stays signed in through June 30.");
      void offerAfterSignIn();
    } catch (e) {
      setErr((e as Error).message);
      busy(f, false);
      const cd = $<HTMLInputElement>("#cd");
      if (cd) {
        cd.value = "";
        cd.focus();
      }
    }
  },
});
